import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { api, COMPANY } from "@/lib/apiClient";
import { toast } from "sonner";

const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const TAHUN_AWAL = 2020;
const TAHUN = Array.from(
  { length: new Date().getFullYear() + 5 - TAHUN_AWAL + 1 },
  (_, i) => TAHUN_AWAL + i
);

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const ringkas = (v) => Number(v || 0).toLocaleString("id-ID");

const Panel = ({ title, sub, children, className = "", testid }) => (
  <div className={`glass rounded-2xl p-5 ${className}`} data-testid={testid}>
    <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
      {title}
    </h4>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const KatList = ({ items, empty, warna }) => (
  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
    {items.length === 0 && <p className="text-xs text-slate-500">{empty}</p>}
    {items.map((k) => (
      <div
        key={k.kategori}
        className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-slate-900/60 px-3 py-1.5"
      >
        <span className="text-[11px] uppercase tracking-wider text-slate-400">{k.kategori}</span>
        <span className={`text-xs font-semibold ${warna}`}>{rp(k.total)}</span>
      </div>
    ))}
  </div>
);

export const DashboardOverview = () => {
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/dashboard-overview", { params: { bulan, tahun } });
      setData(d);
    } catch {
      toast.error("Gagal memuat data dashboard");
    }
  }, [bulan, tahun]);

  useEffect(() => {
    load();
  }, [load]);

  const chartPayment = useMemo(
    () => BULAN.map((b, i) => ({ bulan: b, nilai: data?.payment_bulanan?.[i] || 0 })),
    [data]
  );
  const chartJo = useMemo(
    () => BULAN.map((b, i) => ({ bulan: b, jo: data?.jo_bulanan?.[i] || 0 })),
    [data]
  );

  const totalPemasukan = (data?.rekap_pemasukan || []).reduce((a, k) => a + k.total, 0);
  const totalPengeluaran = (data?.rekap_pengeluaran || []).reduce((a, k) => a + k.total, 0);

  return (
    <div className="space-y-5" data-testid="dashboard-overview">
      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        <div className="space-y-5">
          <div className="glass rounded-2xl p-5 flex items-center justify-center">
            <img src={COMPANY.logo} alt="PAS Percetakan" className="w-32 h-auto" />
          </div>
          <Panel title="Bulan" testid="dash-filter-bulan">
            <div className="grid grid-cols-2 gap-2">
              {BULAN.map((b, i) => (
                <button
                  key={b}
                  type="button"
                  data-testid={`dash-bulan-${i + 1}`}
                  onClick={() => setBulan(bulan === i + 1 ? null : i + 1)}
                  className={`h-9 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                    bulan === i + 1
                      ? "bg-blue-600 text-white"
                      : "border border-white/10 bg-slate-900/70 text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="dash-bulan-semua"
              onClick={() => setBulan(null)}
              className={`mt-2 h-9 w-full rounded-lg text-[11px] font-semibold uppercase tracking-wider ${
                bulan ? "border border-white/10 bg-slate-900/70 text-slate-300" : "bg-blue-600 text-white"
              }`}
            >
              Semua Bulan
            </button>
          </Panel>
          <Panel title="Tahun" testid="dash-filter-tahun">
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
              {TAHUN.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`dash-tahun-${t}`}
                  onClick={() => setTahun(t)}
                  className={`h-9 rounded-lg text-[11px] font-semibold tracking-wider transition-colors duration-200 ${
                    tahun === t
                      ? "bg-amber-500 text-slate-900"
                      : "border border-white/10 bg-slate-900/70 text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Panel>
        </div>

        <div className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            <Panel
              title="Rekap Pemasukan per Kategori"
              sub="dari payment faktur jual · kolom nilai"
              testid="dash-rekap-pemasukan"
            >
              <div className="font-display text-2xl font-light text-emerald-300">
                {rp(totalPemasukan)}
              </div>
              <div className="mt-3">
                <KatList
                  items={data?.rekap_pemasukan || []}
                  empty="Belum ada pemasukan pada periode ini."
                  warna="text-emerald-300"
                />
              </div>
            </Panel>
            <Panel
              title="Rekap Pengeluaran per Kategori"              sub="dari laporan admin · pengeluaran per kategori"
              testid="dash-rekap-pengeluaran"
            >
              <div className="font-display text-2xl font-light text-rose-300">
                {rp(totalPengeluaran)}
              </div>
              <div className="mt-3">
                <KatList
                  items={data?.rekap_pengeluaran || []}
                  empty="Belum ada pengeluaran pada periode ini."
                  warna="text-rose-300"
                />
              </div>
            </Panel>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <Panel title="Total Payment Masuk" sub="seluruh laporan admin" testid="dash-total-payment">
              <div className="font-display text-3xl font-light text-slate-100">
                {ringkas(data?.total_payment_all)}
              </div>
              <p className="text-[10px] uppercase tracking-wider text-amber-400 mt-1">Rupiah</p>
              <div className="mt-3 border-t border-white/10 pt-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">
                  Payment pada periode filter
                </p>
                <div className="font-display text-xl font-light text-emerald-300">
                  {ringkas(data?.total_payment_filter)}
                </div>
              </div>
            </Panel>
            <Panel title="Total Piutang Keseluruhan" sub="faktur jual · kolom kredit" testid="dash-total-piutang">
              <div className="font-display text-3xl font-light text-slate-100">
                {ringkas(data?.total_piutang_all)}
              </div>
              <p className="text-[10px] uppercase tracking-wider text-amber-400 mt-1">Rupiah</p>
              <div className="mt-3 border-t border-white/10 pt-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">
                  Piutang pada periode filter
                </p>
                <div className="font-display text-xl font-light text-rose-300">
                  {ringkas(data?.total_piutang_filter)}
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <Panel
            title="Total Payment Masuk per Bulan"
            sub={`Tahun ${tahun}`}
            testid="dash-chart-payment"
          >
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartPayment}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="bulan" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => ringkas(v)} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 12 }}
                    formatter={(v) => rp(v)}
                  />
                  <Line type="monotone" dataKey="nilai" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Total JO Masuk per Bulan" sub={`Tahun ${tahun}`} testid="dash-chart-jo">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartJo}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="bulan" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="jo" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel
            title="Total Tagihan Bahan Belum Dibayar"
            sub="stok keluar × harga jual · hanya kategori yang masih ada tagihan"
            testid="dash-tagihan-bahan"
          >
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-xs">
                <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
                  <tr>
                    {["Kategori", "Total", "Terbayar", "Belum Bayar", "Status"].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="dash-tagihan-bahan-list">
                  {(data?.tagihan_bahan || []).filter((b) => b.belum_bayar > 0).length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                        Tidak ada tagihan bahan yang belum dibayar.
                      </td>
                    </tr>
                  )}
                  {(data?.tagihan_bahan || [])
                    .filter((b) => b.belum_bayar > 0)
                    .map((b) => (
                    <tr key={b.kategori} className="border-t border-white/5">
                      <td className="px-3 py-2 text-slate-200 font-medium">{b.kategori}</td>
                      <td className="px-3 py-2">{rp(b.total)}</td>
                      <td className="px-3 py-2 text-emerald-300">{rp(b.terbayar)}</td>
                      <td className="px-3 py-2 text-rose-300 font-semibold">{rp(b.belum_bayar)}</td>
                      <td className="px-3 py-2 text-[10px] uppercase text-slate-400">{b.status}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/10 bg-slate-900/60 font-semibold">
                    <td className="px-3 py-2" colSpan={3}>TOTAL BELUM DIBAYAR</td>
                    <td className="px-3 py-2 text-rose-300" data-testid="dash-tagihan-bahan-total">
                      {rp(
                        (data?.tagihan_bahan || []).reduce((a, b) => a + Number(b.belum_bayar || 0), 0)
                      )}
                    </td>
                    <td className="px-3 py-2" />
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            title="JO Masuk per Kategori"
            sub={`Periode: ${bulan ? BULAN[bulan - 1] : "semua bulan"} ${tahun}`}
            testid="dash-chart-kategori"
          >
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.jo_kategori || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="kategori" stroke="#64748b" fontSize={10} interval={0} angle={-15} height={45} />
                  <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 12 }}
                  />
                  <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <Panel title="Total Tagihan" sub="rekap piutang client · urut terbesar" testid="dash-tagihan">
          <div className="max-h-[560px] overflow-y-auto pr-1">
            <table className="w-full text-xs">
              <thead className="text-slate-400 uppercase text-[10px] tracking-wider sticky top-0 bg-slate-900/95">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold">No</th>
                  <th className="px-2 py-2 text-left font-semibold">Nama Client</th>
                  <th className="px-2 py-2 text-right font-semibold">Jumlah Tagihan</th>
                </tr>
              </thead>
              <tbody data-testid="dash-tagihan-list">
                {(data?.tagihan_client || []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-2 py-6 text-center text-slate-500">
                      Tidak ada tagihan tersisa.
                    </td>
                  </tr>
                )}
                {(data?.tagihan_client || []).map((c, i) => (
                  <tr key={c.client_name} className="border-t border-white/5">
                    <td className="px-2 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-2 py-2 text-slate-200">{c.client_name}</td>
                    <td className="px-2 py-2 text-right text-rose-300 font-semibold">{rp(c.sisa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
};
